"use client";
import {
    Alert,
    Box,
    Card,
    CardContent,
    CardMedia,
    Container,
    Divider,
    Link as MuiLink,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import Page from "../../component/Page";

const HERO_IMAGE = {
    src: "/recap/2025/image001.png",
    alt: "Buffalo Trace Distillery, Frankfort, April 2025",
    caption:
        "(Buffalo Trace Distillery, Frankfort, April 2025. Photo courtesy NWS Louisville)",
};

const FACEBOOK_URL = "https://www.facebook.com/kymesonet/";

const EVENTS = [
    {
        id: 1,
        title: (
            <>
                Jan. 5<sup>th</sup>-6<sup>th</sup> Messy Mixture of Snow, Sleet, Freezing
                Rain, and Rain.
            </>
        ),
        href: "https://www.dnr.state.mn.us/climate/journal/fire-weather-may-10-13-2025.html",
        description: (
            <>
                A potent wave of low pressure moved into the lower Ohio Valley early on
                the morning of Jan. 5<sup>th</sup>. Moisture overrunning cold, arctic air
                at the surface resulted in a potpourri of precipitation. Heavy snow fell
                across portions of north-central Kentucky into the Blue Grass region.
                Louisville officially saw 10.5” of snow, with 9” of it falling on the 5
                <sup>th</sup> (a daily record and the third-snowiest calendar day ever for
                the city). Elsewhere, Covington picked up 10.6” of snow, while Lexington
                received 6.8”. Significant icing also occurred across parts of central
                and southern Kentucky. Ice accretion of over ¼” weighed down trees and
                power lines, resulting in numerous power outages as well as hazardous
                travel.
            </>
        ),
        image: {
            src: "/recap/2025/image002.jpg",
            alt: "Mercer County, January 2025",
            caption: "(Mercer County, January 2025. Photo courtesy NWS Louisville)",
        },
    },
    {
        id: 2,
        title: (
            <>
                February 15<sup>th</sup> – 16<sup>th</sup> Flooding.
            </>
        ),
        href: "https://www.dnr.state.mn.us/climate/journal/fire-weather-may-10-13-2025.html",
        description: (
            <>
                A frontal system stalled over southern Kentucky on the morning of Feb. 15
                <sup>th</sup>. This front served as the focal point for widespread
                showers and thunderstorms. Torrential rainfall fell across much of the
                state. Amounts ranged from 2-6” for most, with the highest totals noted
                across the western region. Significant flash flooding, areal flooding,
                and river flooding occurred. Cold, arctic air dropped into Kentucky on
                the morning of the 16<sup>th</sup>, resulting in some snow along with a
                ‘flash freeze’ as the system departed. Some two dozen fatalities were
                blamed on the storm system, with numerous schools, businesses and roads
                closed for a time.
            </>
        ),
        image: {
            src: "/recap/2025/image003.jpg",
            alt: "I-69 rockslide near Dawson Springs, February 2025",
            caption:
                "(I-69 rockslide near Dawson Springs, February 2025. Photo courtesy NWS Paducah)",
        },
    },
    {
        id: 3,
        title: <>Early April Flooding and Severe Weather.</>,
        href: "https://www.dnr.state.mn.us/climate/journal/unusual-fall-hot-streak-2025.html",
        description: (
            <>
                On April 2<sup>nd</sup>, a cold front eased into Kentucky. This boundary
                became stationary, meandering over the state for several days. Waves of
                rain and thunderstorms moved over Kentucky between the 2<sup>nd</sup> and
                the 6<sup>th</sup>. By the time the system finally exited on the morning
                of the 7<sup>th</sup>, upwards of 10” of rain had accumulated over a
                four-to-five-day span in numerous counties across the central and western
                regions. The KY Mesonet station in Marshall County near Benton received
                the highest rainfall total between the 2<sup>nd</sup> and the 6
                <sup>th</sup> of 15.86”, setting a multi-day rainfall record. Flash
                flooding, areal flooding, and river flooding were all common. The
                Kentucky River in Frankfort crested at 48.3’, its second-highest crest
                all-time there, while upstream at Camp Nelson, a record crest of 47’ was
                measured. Numerous gages along the Green River measured crests that
                ranked among the highest ever recorded at each location. Several
                fatalities were blamed on the flooding, with numerous road closures as
                well as businesses and schools shut down for a time. In addition to the
                heavy rains, this system also produced rounds of severe thunderstorms
                with several tornadoes. The strongest tornado touched down in
                Jeffersontown, a suburb of Louisville, where an EF-3 with winds to 145
                mph was confirmed on the 2<sup>nd</sup>.
            </>
        ),
        image: {
            src: "/recap/2025/image004.jpg",
            alt: "Henry County flooding, April 2025",
            caption: "(Henry County flooding, April 2025. Photo courtesy NWS Louisville)",
        },
    },
    {
        id: 4,
        title: (
            <>
                May 16<sup>th</sup> Severe Weather Outbreak.
            </>
        ),
        href: "https://www.dnr.state.mn.us/climate/journal/smoky-summer-2025.html",
        description: (
            <>
                Spring was stormy, but the season’s most prolific severe-storm producer
                blew through the Commonwealth in mid-May. Thunderstorms brought damaging
                winds and large hail to some. A wind gust to 82 mph was clocked the
                Muhammed Ali International Airport in Louisville. However, the most
                notable event from this outbreak was the violent, long-tracked tornado
                that carved a 60-mile-long path through Russell, Pulaski, and Laurel
                Counties late on the night on the 16<sup>th</sup>. Some of the most
                significant damage occurred on the south side of London, KY. This tornado
                took 19 lives and was rated EF-4 with winds as high as 170 mph. This was
                the strongest tornado to strike the state’s mountainous eastern region
                since April 3, 1974.
            </>
        ),
        image: {
            src: "/recap/2025/image005.jpg",
            alt: "May 16, 2025 Eastern KY tornado",
            caption: "(May 16, 2025 Eastern KY tornado. Photo courtesy NWS Jackson)",
        },
    },
    {
        id: 5,
        title: <>Record Dry August Leads to Late Summer/Early Autumn Drought.</>,
        href: "https://www.dnr.state.mn.us/climate/journal/smoky-summer-2025.html",
        description: (
            <>
                August 2025 was the state’s driest August, dating back to 1895, with an
                average rainfall of only 1.29”. Many locations failed to receive
                one-quarter inch of rain for the month. The KY Mesonet site near
                Hardinsburg in Breckinridge County went the entire month without
                measuring a drop of rainfall. The persistence of abnormally dry
                conditions led to the development of drought for a 4<sup>th</sup>-straight
                year in Kentucky. The drought reached its peak in September. At one
                point, roughly 68% of the Commonwealth was classified as being officially
                in drought per the National Integrated Drought Information System (NIDIS).
                A small section of the western region even slipped into ‘Extreme Drought’
                (D3 classification) in September before significant rains returned to the
                state.
            </>
        ),
        image: {
            src: "/recap/2025/image006.jpg",
            alt: "Drought image",
            caption: undefined,
        },
    },
];

function Hero() {
    return (
        <Paper
            variant="outlined"
            sx={{
                overflow: "hidden",
                borderRadius: 3,
            }}
        >
            <Box sx={{ p: { xs: 2.5, md: 4 } }}>
                <Stack spacing={1.25}>
                    <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                        Kentucky’s Top 5 Weather Events of 2025
                    </Typography>

                    <Typography variant="body1" color="text.secondary">
                        From floods to drought, from winter storms and severe storms, 2025
                        delivered more than its share of significant, memorable weather to
                        the Commonwealth. Below is a list comprised by staff at the Kentucky
                        Climate Center of some of the year’s biggest events, listed
                        chronologically.
                    </Typography>

                    <Alert severity="info" sx={{ mt: 1 }}>
                        We would like <b>YOUR</b> input. Which weather event do you believe
                        stood out as the year’s biggest? Visit our{" "}
                        <MuiLink href={FACEBOOK_URL} target="_blank" rel="noreferrer">
                            Facebook
                        </MuiLink>{" "}
                        page and vote for your own top five weather events for Kentucky.
                    </Alert>
                </Stack>
            </Box>

            <Divider />

            <Box sx={{ p: { xs: 2.5, md: 4 } }}>
                <Stack spacing={1.25}>
                    <Card sx={{ borderRadius: 3 }} variant="outlined">
                        <CardMedia
                            component="img"
                            src={HERO_IMAGE.src}
                            alt={HERO_IMAGE.alt}
                            sx={{ maxHeight: 520, objectFit: "cover" }}
                        />
                    </Card>
                    {HERO_IMAGE.caption ? (
                        <Typography variant="caption" color="text.secondary" align="center">
                            {HERO_IMAGE.caption}
                        </Typography>
                    ) : null}
                </Stack>
            </Box>
        </Paper>
    );
}

function EventCard({ item }) {
    const title = item.href ? (
        <MuiLink href={item.href} target="_blank" rel="noreferrer" underline="hover">
            {item.title}
        </MuiLink>
    ) : (
        item.title
    );

    return (
        <Card variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack spacing={1.25}>
                    <Typography variant="overline" color="text.secondary">
                        Event {item.id}
                    </Typography>

                    <Typography variant="h5" sx={{ fontWeight: 750, lineHeight: 1.2 }}>
                        {title}
                    </Typography>

                    <Typography variant="body1" sx={{ lineHeight: 1.75 }}>
                        {item.description}
                    </Typography>
                </Stack>
            </CardContent>

            {item.image ? (
                <>
                    <Divider />
                    <Box sx={{ p: { xs: 2.5, md: 3 } }}>
                        <Stack spacing={1.25} alignItems="center">
                            <Box
                                component="img"
                                src={item.image.src}
                                alt={item.image.alt}
                                sx={{
                                    width: "100%",
                                    maxWidth: 860,
                                    borderRadius: 2,
                                    border: (t) => `1px solid ${t.palette.divider}`,
                                }}
                            />
                            {item.image.caption ? (
                                <Typography variant="caption" color="text.secondary" align="center">
                                    {item.image.caption}
                                </Typography>
                            ) : null}
                        </Stack>
                    </Box>
                </>
            ) : null}
        </Card>
    );
}

export default function TopWeatherEvents2025Page() {
    return (
        <Page>

            <Box sx={{ py: { xs: 3, md: 6 } }}>
                <Container maxWidth="xl">
                    <Stack spacing={3}>
                        <Hero />

                        <Stack spacing={2}>
                            {EVENTS.map((e) => (
                                <EventCard key={e.id} item={e} />
                            ))}
                        </Stack>
                    </Stack>
                </Container>
            </Box>
        </Page>

    );
}